import * as THREE from 'three';
import { COLORS, SIMULATION } from '../game/constants';

/** Sentinel for bullets with no enemy owner (bosses, scripted spawns). */
const NO_OWNER = -1;

export class BulletSystem {
  readonly group = new THREE.Group();
  private readonly active = new Uint8Array(SIMULATION.maxEnemyBullets);
  private readonly x = new Float32Array(SIMULATION.maxEnemyBullets);
  private readonly y = new Float32Array(SIMULATION.maxEnemyBullets);
  private readonly vx = new Float32Array(SIMULATION.maxEnemyBullets);
  private readonly vy = new Float32Array(SIMULATION.maxEnemyBullets);
  private readonly radius = new Float32Array(SIMULATION.maxEnemyBullets);
  private readonly life = new Float32Array(SIMULATION.maxEnemyBullets);
  /**
   * Index of the enemy that fired this bullet, or NO_OWNER.
   *
   * Crossfire excludes the SHOOTER specifically rather than using a timed
   * arming delay. A delay long enough to protect the shooter is also long
   * enough for the bullet to clear its own formation — which is why the first
   * implementation registered almost nothing in real play.
   */
  private readonly owner = new Int16Array(SIMULATION.maxEnemyBullets);
  private readonly shellGeometry = new THREE.CircleGeometry(1, 10);
  private readonly coreGeometry = new THREE.CircleGeometry(1, 8);
  private readonly shellMaterial = new THREE.MeshBasicMaterial({
    color: COLORS.hostile,
    transparent: true,
    opacity: 0.88,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false,
  });
  private readonly coreMaterial = new THREE.MeshBasicMaterial({
    color: COLORS.hostileCore,
    depthWrite: false,
    toneMapped: false,
  });
  private readonly shell = new THREE.InstancedMesh(
    this.shellGeometry,
    this.shellMaterial,
    SIMULATION.maxEnemyBullets,
  );
  private readonly core = new THREE.InstancedMesh(
    this.coreGeometry,
    this.coreMaterial,
    SIMULATION.maxEnemyBullets,
  );
  private readonly dummy = new THREE.Object3D();
  count = 0;

  constructor() {
    this.group.name = 'EnemyBulletField';
    this.shell.frustumCulled = false;
    this.core.frustumCulled = false;
    this.shell.renderOrder = 20;
    this.core.renderOrder = 21;
    this.group.add(this.shell, this.core);
  }

  spawn(x: number, y: number, vx: number, vy: number, radius = 0.2, life = 8, owner = NO_OWNER): void {
    const index = this.findFree();
    if (index < 0) return;
    this.active[index] = 1;
    this.x[index] = x;
    this.y[index] = y;
    this.vx[index] = vx;
    this.vy[index] = vy;
    this.radius[index] = radius;
    this.life[index] = life;
    this.owner[index] = owner;
    this.count += 1;
  }

  spawnFan(
    x: number,
    y: number,
    targetX: number,
    targetY: number,
    count: number,
    arc: number,
    speed: number,
  ): void {
    const base = Math.atan2(targetY - y, targetX - x);
    const denominator = Math.max(1, count - 1);
    for (let index = 0; index < count; index += 1) {
      const angle = base + arc * (index / denominator - 0.5);
      this.spawn(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed, 0.21, 8);
    }
  }

  spawnRing(x: number, y: number, count: number, speed: number, phase = 0): void {
    for (let index = 0; index < count; index += 1) {
      const angle = phase + (Math.PI * 2 * index) / count;
      this.spawn(x, y, Math.cos(angle) * speed, Math.sin(angle) * speed, 0.18, 8);
    }
  }

  update(delta: number): void {
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      this.x[index] += this.vx[index] * delta;
      this.y[index] += this.vy[index] * delta;
      this.life[index] -= delta;
      if (
        this.life[index] <= 0 ||
        this.y[index] < -28 ||
        this.y[index] > 29 ||
        Math.abs(this.x[index]) > 25
      ) {
        this.active[index] = 0;
        this.count -= 1;
      }
    }
  }

  collidePlayer(px: number, py: number, playerRadius: number): boolean {
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      const dx = this.x[index] - px;
      const dy = this.y[index] - py;
      const collisionRadius = playerRadius + this.radius[index] * 0.72;
      if (dx * dx + dy * dy <= collisionRadius * collisionRadius) {
        this.active[index] = 0;
        this.count -= 1;
        return true;
      }
    }
    return false;
  }

  clear(): number {
    const cleared = this.count;
    this.active.fill(0);
    this.count = 0;
    return cleared;
  }

  /**
   * Snapshot the live bullet field into a flat array: [x, y, vx, vy, r, ...].
   * Used by the automated playtest bot to evaluate threat and by the offline
   * wave validator to prove a survivable path exists. Dev tooling only — the
   * shipping game never calls this.
   */
  /**
   * CROSSFIRE — enemy fire damages enemies.
   *
   * The Lattice is an inventory process, not a commander. It does not value
   * its units and will happily shoot through them, so a wave that boxes the
   * player in also boxes ITSELF in. Dodging becomes an offensive act: pull a
   * volley across a formation and it thins itself.
   *
   * Two rules keep it honest:
   *   - A bullet must have lived `CROSSFIRE_ARM_SECONDS` before it can hurt an
   *     enemy, so nothing kills the unit that fired it.
   *   - Crossfire damage is reduced, so waves cannot wipe themselves out and
   *     rob the player of the kill.
   *
   * @param onHit called with (x, y, killed) so the caller can score and spark.
   */
  resolveCrossfire(
    count: number,
    active: Uint8Array,
    ex: Float32Array,
    ey: Float32Array,
    ehp: Float32Array,
    radiusOf: (index: number) => number,
    damage: number,
    onHit: (x: number, y: number, enemyIndex: number, killed: boolean) => void,
  ): void {
    for (let b = 0; b < this.active.length; b += 1) {
      if (!this.active[b]) continue;
      const shooter = this.owner[b];
      const bx = this.x[b];
      const by = this.y[b];
      const br = this.radius[b];

      for (let e = 0; e < count; e += 1) {
        if (!active[e]) continue;
        // A unit is never hurt by its own round — but everything else on the
        // field is fair game from the moment the bullet exists.
        if (e === shooter) continue;
        const dx = ex[e] - bx;
        const dy = ey[e] - by;
        const r = radiusOf(e) + br;
        if (dx * dx + dy * dy > r * r) continue;

        ehp[e] -= damage;
        const killed = ehp[e] <= 0;
        onHit(ex[e], ey[e], e, killed);
        // The bullet is spent either way — it hit something solid.
        this.active[b] = 0;
        this.count -= 1;
        break;
      }
    }
  }

  snapshot(out: Float32Array): number {
    let n = 0;
    for (let i = 0; i < this.active.length; i += 1) {
      if (!this.active[i]) continue;
      if (n * 5 + 4 >= out.length) break;
      out[n * 5] = this.x[i];
      out[n * 5 + 1] = this.y[i];
      out[n * 5 + 2] = this.vx[i];
      out[n * 5 + 3] = this.vy[i];
      out[n * 5 + 4] = this.radius[i];
      n += 1;
    }
    return n;
  }

  syncVisuals(): void {
    let visualIndex = 0;
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) continue;
      const radius = this.radius[index];
      this.dummy.position.set(this.x[index], this.y[index], 1.2);
      this.dummy.scale.set(radius, radius, 1);
      this.dummy.updateMatrix();
      this.shell.setMatrixAt(visualIndex, this.dummy.matrix);
      this.dummy.scale.set(radius * 0.36, radius * 0.36, 1);
      this.dummy.position.z = 1.22;
      this.dummy.updateMatrix();
      this.core.setMatrixAt(visualIndex, this.dummy.matrix);
      visualIndex += 1;
    }
    this.shell.count = visualIndex;
    this.core.count = visualIndex;
    this.shell.instanceMatrix.needsUpdate = true;
    this.core.instanceMatrix.needsUpdate = true;
  }

  reset(): void {
    this.active.fill(0);
    this.count = 0;
    this.syncVisuals();
  }

  dispose(): void {
    this.shellGeometry.dispose();
    this.coreGeometry.dispose();
    this.shellMaterial.dispose();
    this.coreMaterial.dispose();
  }

  private findFree(): number {
    for (let index = 0; index < this.active.length; index += 1) {
      if (!this.active[index]) return index;
    }
    return -1;
  }
}
